import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Divider,
  styled,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { FormikErrors, useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import CustomModal from '../../Common/CustomModal';
import LabeledInput from '../../Common/LabeledInput';
import StyledBackButton from '../../Common/StyledBackButton';
import StyledButton from '../../Common/StyledButton';
import { useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/rootReducer';
import SearchInput from '../../Common/SearchInput';
import { chatSlimScrollbarStyles } from '../../../utils/styles';
import {
  ICreateUserGroupPayload,
  IModule,
  IUserPermission,
  userGroupsActions,
} from '../../../redux/userGroups/userGroupsSlice';
import { moduleDescriptions, userPermissionDescriptions } from '../../../types/userGroups';

const StyledCheckbox = styled(Checkbox)({
  color: '#6CA3AC',
  '&.Mui-checked': {
    color: '#6CA3AC',
  },
});

const StyledFormControlLabel = styled(FormControlLabel)({
  margin: 0,
  width: '100%',
  justifyContent: 'space-between',
  marginBottom: '16px',
  '& .MuiFormControlLabel-label.Mui-disabled': {
    color: 'silver',
  },
});

interface CreateUpdateUserGroupModalProps {
  isOpen: boolean;
  isCreateGroup: boolean;
  id?: string;
  modulesWithPermissions?: IModule[];
  initialGroupName: string;
  handleDismiss: () => void;
}

interface SelectedPermission {
  id: string;
  selectedPermissions: string[];
}

const CreateUpdateUserGroupModal = ({
  isOpen,
  isCreateGroup,
  initialGroupName,
  modulesWithPermissions,
  handleDismiss,
  id,
}: CreateUpdateUserGroupModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { modules, fetchModulesLoading, createUserGroupLoading, createUserGroupSuccess } =
    useAppSelector((state: RootState) => state.userGroups);

  const [selectedModule, setSelectedModule] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [permissions, setPermissions] = useState<IUserPermission[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<SelectedPermission[]>([]);

  const initialValues: ICreateUserGroupPayload = {
    id,
    name: initialGroupName,
    userPermissionIds: [],
  };
  const formik = useFormik({
    initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
    validationSchema: Yup.object().shape({
      name: Yup.string().required(
        t('userGroups.createUpdateUserGroup.validations.groupNameRequired')
      ),
      userPermissionIds: Yup.array()
        .min(1, t('userGroups.createUpdateUserGroup.validations.permissionsRequired'))
        .required(),
    }),
    validate: (values) => {
      if (!values.name) {
        formik.setErrors({
          name: t('userGroups.createUpdateUserGroup.validations.groupNameRequired'),
        });
      } else {
        setTimeout(() => {
          formik.setErrors(((prev) => ({
            ...prev,
            name: undefined,
          })) as FormikErrors<ICreateUserGroupPayload>);
        }, 2000);
      }
      if (values.userPermissionIds.length < 1) {
        formik.setErrors(((prevErrors) => ({
          ...prevErrors,
          userPermissionIds: t('userGroups.createUpdateUserGroup.validations.permissionsRequired'),
        })) as FormikErrors<ICreateUserGroupPayload>);

        setTimeout(() => {
          formik.setErrors(((prevErrors) => ({
            ...prevErrors,
            userPermissionIds: undefined,
          })) as FormikErrors<ICreateUserGroupPayload>);
        }, 2000);
      }
    },
    onSubmit: (values) => {
      dispatch(userGroupsActions.createUpdateUserGroup({ values, isCreate: isCreateGroup }));
    },
  });

  const isAllSelected = (moduleId: string) => {
    if (!permissions.length) return false;

    const currentModule = selectedPermissions?.find((i) => i.id === moduleId);
    if (!currentModule) return false;

    const allPermissions = modules.find((m) => m.id === moduleId)?.UserPermissions;
    if (!allPermissions) return false;
    return allPermissions.every((p) => currentModule?.selectedPermissions?.includes(p.id));
  };

  const onModuleSelectAll = (checked: boolean, moduleId: string) => {
    if (!selectedPermissions?.length) return;

    const currentModuleIndex = selectedPermissions?.findIndex((i) => i.id === moduleId);
    if (currentModuleIndex < 0) return;

    if (!checked) {
      selectedPermissions[currentModuleIndex].selectedPermissions = [];
      setSelectedPermissions([...selectedPermissions]);
      setSearchTerm('');
      return;
    }

    const allModulePermissions = modules.find((m) => m.id === moduleId);
    if (!allModulePermissions) return;
    selectedPermissions[currentModuleIndex].selectedPermissions =
      allModulePermissions?.UserPermissions.map((p) => p.id);
    setSelectedPermissions([...selectedPermissions]);
    setSearchTerm('');
  };

  const setInitialUseState = () => {
    if (modules.length > 0) {
      const newValue = modules.map((v) => {
        const initialPermissions: SelectedPermission = {
          id: v.id,
          selectedPermissions: [],
        };

        if (isCreateGroup) return initialPermissions;

        const module = modulesWithPermissions?.find((i) => i.id === v.id);
        if (!module) return initialPermissions;
        initialPermissions.selectedPermissions = module.UserPermissions.map((j) => j.id) ?? [];

        return initialPermissions;
      });
      if (newValue) {
        setSelectedPermissions(newValue);
        setSelectedModule(modules[0].id);
      }
    }
    setSearchTerm('');
  };

  const handleDismissOnClick = () => {
    formik.resetForm();
    setInitialUseState();
    handleDismiss();
  };

  useEffect(() => {
    const userPermissionIds: string[] = [];
    selectedPermissions.forEach((v) => {
      v.selectedPermissions.forEach((i) => userPermissionIds.push(i));
    });

    formik.setFieldValue('userPermissionIds', userPermissionIds);
  }, [selectedPermissions]);

  useEffect(() => {
    const newValue = modules.find((v) => v.id === selectedModule)?.UserPermissions;
    if (newValue) setPermissions(newValue);
  }, [selectedModule]);

  useEffect(() => {
    setInitialUseState();
  }, [modules]);

  useEffect(() => {
    if (isOpen) {
      dispatch(userGroupsActions.fetchAllModules());
      if (modules?.length) {
        setInitialUseState();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const debounceSearch = setTimeout(() => {
      const newValue = modules
        .find((v) => v.id === selectedModule)
        ?.UserPermissions.filter((v) =>
          t(userPermissionDescriptions[v.permissionCode])
            .toLocaleLowerCase()
            .includes(searchTerm.toLocaleLowerCase())
        );
      if (newValue) setPermissions(newValue);
    }, 500);
    return () => clearTimeout(debounceSearch);
  }, [searchTerm]);

  useEffect(() => {
    if (createUserGroupSuccess) {
      formik.resetForm();
    }
  }, [createUserGroupSuccess]);

  return (
    <CustomModal isOpen={isOpen} width="97vw">
      <form onSubmit={formik.handleSubmit} noValidate>
        <Grid container p={4} alignContent="space-between" width="97vw" height="98vh">
          <Grid item xs={12}>
            <LabeledInput
              label=""
              id="group-name"
              type="text"
              name="name"
              value={formik.values.name}
              handleChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.name ?? undefined}
              fontSize="28px !important"
              fontWeight="700 !important"
              color="primary.main"
            />
            <Divider sx={{ backgroundColor: '#EEEEEE', mt: 3 }} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="title3Regular" fontWeight={700}>
              {t('userGroups.createUpdateUserGroup.permissions')}
            </Typography>
            <FormHelperText sx={{ color: 'red' }}>{formik.errors.userPermissionIds}</FormHelperText>
            <Grid container borderRadius="10px" border="0.5px solid #FFDDC8" height="60vh" mt={2}>
              <Grid
                item
                container
                xs={2.4}
                height="100%"
                sx={{ overflowY: 'auto', ...chatSlimScrollbarStyles }}
              >
                {modules?.map((v, index) => (
                  <Grid
                    item
                    key={v.id}
                    p={2}
                    minHeight={54}
                    width="100%"
                    borderBottom="0.5px solid #7CAEBE80"
                    sx={{
                      cursor: 'pointer',
                      background:
                        selectedModule === v.id
                          ? ` linear-gradient(
                            137.65deg, 
                            rgba(101, 153, 165, 0.15) 24.11%, 
                            rgba(59, 102, 126, 0.15) 83.2%
                          )`
                          : '#FFF',
                      color: 'primary.main',
                      fontWeight: selectedModule === v.id ? 700 : 400,
                      borderTopLeftRadius: index === 0 ? '10px' : '0px',
                    }}
                    onClick={() => {
                      setSelectedModule(v.id);
                    }}
                  >
                    {`${t(moduleDescriptions[v.name])} (${
                      selectedPermissions.find((i) => i.id === v.id)?.selectedPermissions.length ??
                      0
                    }/${v.UserPermissions.length})`}
                  </Grid>
                ))}
              </Grid>
              <Grid
                item
                container
                xs={9.6}
                borderLeft="0.5px solid #FFDDC8"
                p={3}
                pb={0}
                gap={3}
                flexDirection="column"
                height="100%"
              >
                <StyledFormControlLabel
                  control={
                    <StyledCheckbox
                      checked={isAllSelected(selectedModule)}
                      onChange={(_, checked) => onModuleSelectAll(checked, selectedModule)}
                      inputProps={{ 'aria-label': 'controlled' }}
                      checkedIcon={
                        <Box component="img" src="/assets/icons/check-box-checked.svg" />
                      }
                      icon={<Box component="img" src="/assets/icons/check-box.svg" />}
                      disabled={
                        modules.find((i) => i.id === selectedModule)?.UserPermissions.length === 0
                      }
                    />
                  }
                  value={isAllSelected(selectedModule)}
                  label={t('userGroups.createUpdateUserGroup.grantFullAccess')}
                  labelPlacement="end"
                  sx={{
                    backgroundColor: '#7CAEBE33',
                    paddingRight: '9px',
                    borderRadius: '10px',
                    margin: 0,
                    width: 'max-content',
                  }}
                />
                <SearchInput
                  type="text"
                  placeholder={t('clientTable.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={
                    modules.find((i) => i.id === selectedModule)?.UserPermissions.length === 0
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <img src="/assets/icons/magnifer.svg" alt="search" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': { height: '38px' },
                    '& .MuiOutlinedInput-input.Mui-disabled': {
                      '-webkit-text-fill-color': 'silver !important',
                    },
                  }}
                />
                <Grid
                  container
                  maxHeight="67%"
                  sx={{ overflowY: 'auto', ...chatSlimScrollbarStyles }}
                >
                  {searchTerm !== '' && permissions.length === 0 && (
                    <Grid
                      container
                      height="37vh"
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <img
                        src="/assets/icons/sad-circle.svg"
                        alt="no-results"
                        width={32}
                        height={32}
                      />
                      <Typography variant="calloutRegular" color="#2A3C67">
                        {t('userGroups.addRemoveMemberModal.noResult')}
                      </Typography>
                    </Grid>
                  )}
                  {permissions?.map((item) => (
                    <Grid item xs={4} key={item.id}>
                      <FormControlLabel
                        control={
                          <StyledCheckbox
                            checked={selectedPermissions
                              .find((i) => i.id === selectedModule)
                              ?.selectedPermissions.includes(item.id)}
                            onChange={() => {
                              const includes = selectedPermissions
                                .find((i) => i.id === selectedModule)
                                ?.selectedPermissions.includes(item.id);

                              const newValue = selectedPermissions.map((i) => {
                                if (i.id === selectedModule) {
                                  if (includes) {
                                    const index = i.selectedPermissions.findIndex(
                                      (j) => j === item.id
                                    );
                                    i.selectedPermissions.splice(index, 1);
                                  } else {
                                    i.selectedPermissions.push(item.id);
                                  }
                                }
                                return i;
                              });

                              setSelectedPermissions(newValue);
                            }}
                            inputProps={{ 'aria-label': 'controlled' }}
                            checkedIcon={
                              <Box component="img" src="/assets/icons/check-box-checked.svg" />
                            }
                            icon={<Box component="img" src="/assets/icons/check-box.svg" />}
                          />
                        }
                        label={t(userPermissionDescriptions[item.permissionCode])}
                        labelPlacement="end"
                        sx={{
                          margin: 0,
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ backgroundColor: '#EEEEEE' }} />
          </Grid>
          <Grid item container xs={12} height={46} justifyContent="space-between">
            <StyledBackButton
              disabled={fetchModulesLoading || createUserGroupLoading}
              onClick={handleDismissOnClick}
            >
              {t('common.dismiss')}
            </StyledBackButton>
            <StyledButton
              disabled={fetchModulesLoading}
              loading={createUserGroupLoading}
              loadingText={isCreateGroup ? t('common.creating') : t('common.saving')}
            >
              {isCreateGroup
                ? t('userGroups.createUpdateUserGroup.createGroup')
                : t('userGroups.createUpdateUserGroup.saveChanges')}
            </StyledButton>
          </Grid>
        </Grid>
      </form>
    </CustomModal>
  );
};

export default CreateUpdateUserGroupModal;
